import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InputContext } from '@app/user-content/user-input-v2/input.model';
import { InputsService } from '@app/inputs/services/inputs.service';
import { InputImageUploadAdapterService } from '@app/uploader/upload-section/adapters/input-image-upload-adapter.service';
import { EventBaseFacade } from '../event-base-facade.service';
import { LocalityViewModel } from '@app/shared/components/address-suggest/address-suggest.service';
import { APIResponseModel, ViewModel } from '../../event.model';
import { map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { HTTP_REQUIRED_URL_PATTERN } from '@app/shared/utils';
import {
  contentOwnerIdValidator,
  maxFifteenSkillsValidator,
} from '@app/user-content/user-input-v2/utils/validators';
import { addOwnerDetailsToInput } from '@app/user-content/user-input-v2/utils/content-owner-helper';
import { addSessionLocationToVM } from '@app/user-content/user-input-v2/utils/session-utils';

export const initialVM = {
  // Session Details props
  sessions: [],
  isSessionDetailsToggledOn: false,
  // Advanced Settings props
  orgContentMetadata: {
    hideFromCatalog: false,
    groupIds: [],
  },
};

@Injectable()
export class EventCatalogFacade extends EventBaseFacade {
  private formBuilder = inject(FormBuilder);

  constructor(
    protected override inputsService: InputsService,
    protected override inputImageUploadAdapterService: InputImageUploadAdapterService
  ) {
    super(inputsService, inputImageUploadAdapterService);
  }

  public initializeForm(inputContext: InputContext): Observable<FormGroup> {
    const input$ = inputContext.isEditing
      ? this.eventService
          .getEvent(inputContext.inputId)
          .pipe(
            switchMap((input) =>
              addOwnerDetailsToInput<APIResponseModel>(
                input,
                this.userGroupListService
              )
            )
          )
      : of(undefined);

    return input$.pipe(
      tap((input) => {
        if (!input) {
          return;
        }

        const i18n = this.translateService.instant(['Core_CreatedBy'], {
          authorName: input.createdBy,
        });

        super.patchViewModel({
          ...addSessionLocationToVM(input),
          i18n: { ...this.viewModel.i18n, ...i18n },
          isSessionDetailsToggledOn: !!input.sessions.length,
        });
      }),
      map((input) =>
        this.formBuilder.group({
          addToCatalog: [true],
          createdBy: [input?.createdBy],
          title: [input?.name, [Validators.required]],
          eventUrl: [
            input?.eventUrl,
            [Validators.pattern(HTTP_REQUIRED_URL_PATTERN)],
          ],
          summary: [input?.summary, [Validators.maxLength(3000)]],
          owner: [input?.owner, [Validators.required, contentOwnerIdValidator]],
          skills: [input?.tags ?? [], maxFifteenSkillsValidator],
        })
      ),
      shareReplay({ refCount: false, bufferSize: 1 })
    );
  }

  public override initializeViewModel(
    inputContext: InputContext
  ): Observable<ViewModel> {
    super.initializeViewModel(inputContext);

    const API = {
      onAddressChange: this.onAddressChange.bind(this),
      toggleSessionDetails: this.toggleSessionDetails.bind(this),
      updateDuration: this.updateDuration.bind(this),
      onSubmit: this.onSubmit.bind(this),
    };

    return super
      .patchViewModel({ ...API, ...initialVM })
      .pipe(shareReplay({ refCount: false, bufferSize: 1 }));
  }

  public override onSubmit(form: FormGroup) {
    const submit$ = super.onSubmit(form);

    submit$.subscribe();

    return submit$; // not really used here, but required by the base class
  }

  // Unused in this context, but required by the base class
  public onNext() {
    return null;
  }

  private toggleSessionDetails(isSessionDetailsToggledOn: boolean): void {
    super.patchViewModel({ isSessionDetailsToggledOn });
  }

  private onAddressChange(sessionLocation: LocalityViewModel): void {
    super.patchViewModel({
      sessionLocation,
    });
  }

  /**
   * Update duration form hours/minutes to match session details changes
   */
  private updateDuration(
    { durationHours, durationMinutes },
    form: FormGroup
  ): void {
    form.patchValue({
      durationForm: {
        durationHours,
        durationMinutes,
      },
    });

    // Trigger validation
    form.get('durationForm').updateValueAndValidity();
  }
}
